/**
 * application-form — pure versioning / immutability logic
 * (application-form-builder spec, task 3.1).
 *
 * This module captures an Application_Form's structure as an immutable,
 * deterministic Form_Version snapshot and provides the rules that keep prior
 * submissions interpretable after later edits. It is the I/O-free heart of
 * Requirement 8 (versioning + post-submission immutability) and feeds the
 * Phase (Req 16), per-page approval (Req 21), and audience (Req 22) attributes
 * into the captured snapshot so a response is always interpreted, gated, and
 * projected against the structure it was captured under (Req 8.3).
 *
 * Like its sibling modules (`fieldValidation.ts`, `formStructure.ts`) it holds
 * NO AWS SDK calls and NO I/O — the same code runs in the `application-form`
 * Lambda (`jest` + `fast-check`) and is re-used by the frontend (`vitest`),
 * mirroring the `division-registry/visibility.ts` / `person-service/personLogic.ts`
 * precedents.
 *
 * What lives here:
 *   - `snapshotForm` — assemble ordered pages → ordered fields into a frozen,
 *     deterministic `FormVersionSnapshot` carrying the extended page attributes
 *     (`phase`, `audienceSet`, `requiresDirectorApproval`) and the extended
 *     field attributes (`fieldCategory`, `bindingTarget`, `audienceSet`)
 *     (Req 8.1, 16.1, 21.1, 22.1).
 *   - `diffStructure` — the structural change list between two snapshots
 *     (Req 8.2).
 *   - `immutabilityGuard` — reject deleting / retyping a Custom_Field that has a
 *     stored Response_Value while submissions exist, while allowing additive
 *     edits; Bound_Fields hold no stored value and are exempt by construction
 *     (Req 8.4, 8.5, 8.6).
 *   - `renderResponse` — re-render a stored response through its captured
 *     snapshot, reproducing the stored Response_Values (Req 8.3, 8.7, 9.3).
 *
 * Validates: Requirements 8.1, 8.2, 8.3, 8.4, 8.5, 8.6, 9.3, 16.1, 21.1, 22.1
 */

import type { FieldType } from '../../types/database';
import { normalizeDisplayOrder } from './formStructure';
import type {
  Audience,
  BindingTarget,
  FieldCategory,
  FormFieldDef,
  FormVersionSnapshot,
  HelpTextFormat,
  Phase,
  ResponseValue,
  SnapshotPage,
} from './fieldValidation';

// Re-export the base snapshot types so downstream modules / adapters can import
// the version vocabulary from this module (the snapshot's owning module).
export type {
  Audience,
  BindingTarget,
  FieldCategory,
  FormFieldDef,
  FormVersionSnapshot,
  HelpTextFormat,
  Phase,
  ResponseValue,
  SnapshotPage,
};

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/** Page Phase default when a row leaves it unset (Req 16.1). */
const DEFAULT_PHASE: Phase = 'PHASE_1';
/** Audience_Set default for pages and fields when a row leaves it unset (Req 22.1). */
const DEFAULT_AUDIENCE_SET: readonly Audience[] = ['APPLICANT', 'DIRECTOR'];

// ---------------------------------------------------------------------------
// Input row read-models
//
// Minimal, structural views of the persisted `FormPage` / `FormField` rows
// (see `types/database.ts`). Only the attributes the snapshot needs are
// modeled; the stored rows are a superset.
// ---------------------------------------------------------------------------

/** The page half of `snapshotForm`'s input — a persisted `FormPage` row. */
export interface PageRow {
  id: string;
  formSchemaId?: string;
  competitionId?: string;
  title: string;
  helpText?: string;
  helpTextFormat?: HelpTextFormat; // NEW (rich-help-text) — additive; absent ⇒ PLAIN
  helpTextHtml?: string; // NEW (rich-help-text) — sanitized HTML; present iff helpTextFormat === 'HTML'
  autoCollapseHelp?: boolean; // NEW (rich-help-text) — Auto_Collapse_Config; absent ⇒ false
  displayOrder: number;
  visibleDivisionKeys?: string[] | null;
  audienceSet?: Audience[];
  phase?: Phase;
  requiresDirectorApproval?: boolean;
}

/** The field half of `snapshotForm`'s input — a persisted `FormField` row. */
export interface FormFieldRow {
  id: string;
  formPageId?: string;
  label: string;
  fieldCategory: FieldCategory;
  bindingTarget?: BindingTarget;
  fieldType?: FieldType;
  required: boolean;
  wordLimit?: number;
  characterLimit?: number;
  options?: string[];
  helpText?: string;
  helpTextFormat?: HelpTextFormat; // NEW (rich-help-text) — additive; absent ⇒ PLAIN
  helpTextHtml?: string; // NEW (rich-help-text) — sanitized HTML; present iff helpTextFormat === 'HTML'
  allowedContentTypes?: string[];
  maxFileSizeBytes?: number;
  maxFileCount?: number;
  displayOrder: number;
  visibleDivisionKeys?: string[] | null;
  audienceSet?: Audience[];
}

/** Optional snapshot metadata; derived from the rows when omitted. */
export interface SnapshotMeta {
  formSchemaId?: string;
  versionNumber?: number;
  competitionId?: string;
}

// ---------------------------------------------------------------------------
// diffStructure / immutabilityGuard / renderResponse result shapes
// ---------------------------------------------------------------------------

/** The kind of structural change between two captured versions (Req 8.2). */
export type StructuralChangeKind =
  | 'PAGE_ADDED'
  | 'PAGE_REMOVED'
  | 'PAGE_MODIFIED'
  | 'FIELD_ADDED'
  | 'FIELD_REMOVED'
  | 'FIELD_RETYPED'
  | 'FIELD_MODIFIED';

/** A single structural difference, naming the affected page and/or field. */
export interface StructuralChange {
  kind: StructuralChangeKind;
  pageId?: string;
  fieldId?: string;
  detail: string;
}

/** A single immutability violation, naming the affected Form_Field (Req 8.5). */
export interface ImmutabilityViolation {
  fieldId: string;
  reason: string;
}

/** The result of the immutability guard. */
export interface ImmutabilityGuardResult {
  allowed: boolean;
  violations: ImmutabilityViolation[];
}

/** A field re-rendered through its captured version (Req 9.3). */
export interface RenderedField {
  fieldId: string;
  label: string;
  fieldCategory: FieldCategory;
  bindingTarget?: BindingTarget;
  fieldType?: FieldType;
  required: boolean;
  displayOrder: number;
  /** The stored Response_Value for a CUSTOM field; absent for Bound_Fields. */
  value?: ResponseValue;
}

/** A page re-rendered through its captured version (Req 9.1, 9.3). */
export interface RenderedPage {
  pageId: string;
  title: string;
  helpText?: string;
  displayOrder: number;
  phase: Phase;
  audienceSet: Audience[];
  requiresDirectorApproval: boolean;
  fields: RenderedField[];
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Stable comparator for ordered rows: by `displayOrder` ascending, breaking ties
 * deterministically on `id` so the snapshot is reproducible for identical input.
 */
function byDisplayOrderThenId(
  a: { id: string; displayOrder: number },
  b: { id: string; displayOrder: number },
): number {
  if (a.displayOrder !== b.displayOrder) {
    return a.displayOrder - b.displayOrder;
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Map a persisted field row to the snapshot's `FormFieldDef`, applying defaults
 * and dropping `undefined` so the captured structure is canonical.
 */
function toFieldDef(row: FormFieldRow): FormFieldDef {
  const def: FormFieldDef = {
    id: row.id,
    label: row.label,
    fieldCategory: row.fieldCategory,
    required: row.required,
    audienceSet: [...(row.audienceSet ?? DEFAULT_AUDIENCE_SET)],
  };
  // `!= null` (not `!== undefined`) so an attribute that arrives as JSON `null`
  // — which is how a GraphQL-sourced row represents an absent nullable list or
  // scalar (e.g. a non-select field's `options`) — is treated as ABSENT rather
  // than spread. `[...null]` throws ("null is not iterable"), which previously
  // crashed any caller that fed snapshotForm a GraphQL-shaped row (the builder's
  // live publish-state comparison). Backend rows use `undefined`, so this is
  // behavior-preserving there and strictly more robust for GraphQL callers.
  if (row.bindingTarget != null) def.bindingTarget = row.bindingTarget;
  if (row.fieldType != null) def.fieldType = row.fieldType;
  if (row.wordLimit != null) def.wordLimit = row.wordLimit;
  if (row.characterLimit != null) def.characterLimit = row.characterLimit;
  if (row.options != null) def.options = [...row.options];
  if (row.helpText != null) def.helpText = row.helpText;
  if (row.helpTextFormat != null) def.helpTextFormat = row.helpTextFormat;
  if (row.helpTextHtml != null) def.helpTextHtml = row.helpTextHtml;
  if (row.allowedContentTypes != null) def.allowedContentTypes = [...row.allowedContentTypes];
  if (row.maxFileSizeBytes != null) def.maxFileSizeBytes = row.maxFileSizeBytes;
  if (row.maxFileCount != null) def.maxFileCount = row.maxFileCount;
  if (row.visibleDivisionKeys !== undefined)
    def.visibleDivisionKeys =
      row.visibleDivisionKeys === null ? null : [...row.visibleDivisionKeys];
  return def;
}

/** Recursively freeze a value so a captured snapshot is truly immutable. */
function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const key of Object.keys(value as Record<string, unknown>)) {
      deepFreeze((value as Record<string, unknown>)[key]);
    }
  }
  return value;
}

/** Flatten every field of a snapshot into a `fieldId → { field, pageId }` map. */
function fieldIndex(
  snapshot: FormVersionSnapshot,
): Map<string, { field: FormFieldDef; pageId: string }> {
  const index = new Map<string, { field: FormFieldDef; pageId: string }>();
  for (const page of snapshot.pages) {
    for (const field of page.fields) {
      index.set(field.id, { field, pageId: page.id });
    }
  }
  return index;
}

// ---------------------------------------------------------------------------
// snapshotForm
// ---------------------------------------------------------------------------

/**
 * Capture an Application_Form's structure as a deterministic, immutable
 * `FormVersionSnapshot`. Pages are emitted in ascending display order; each
 * page carries its own fields (those whose `formPageId` matches the page) in
 * ascending display order. Both pages and the fields within each page get a
 * dense, duplicate-free `0..n-1` display order via
 * `formStructure.ts#normalizeDisplayOrder`, so the snapshot is canonical and
 * reproducible for identical input (Req 8.1).
 *
 * Each page carries the extended attributes `phase`, `audienceSet`, and
 * `requiresDirectorApproval`; each field carries `fieldCategory`,
 * `bindingTarget`, and `audienceSet` (Req 16.1, 21.1, 22.1). Fields not
 * assigned to any page in `pages` are omitted from the snapshot.
 *
 * Pure and total; the input arrays are never mutated and the returned snapshot
 * is deep-frozen.
 *
 * @param pages the Application_Form's pages.
 * @param fields the Application_Form's fields (associated by `formPageId`).
 * @param meta  optional snapshot metadata; `formSchemaId` / `competitionId`
 *              fall back to the first page's values and `versionNumber`
 *              defaults to 1.
 */
export function snapshotForm(
  pages: readonly PageRow[],
  fields: readonly FormFieldRow[],
  meta: SnapshotMeta = {},
): FormVersionSnapshot {
  const sortedPages = [...pages].sort(byDisplayOrderThenId);
  const pageOrder = normalizeDisplayOrder(
    sortedPages.map((p) => p.id),
    sortedPages,
  );

  // Bucket fields by their owning page, then order densely within each page.
  const fieldsByPage = new Map<string, FormFieldRow[]>();
  for (const field of fields) {
    if (field.formPageId === undefined) {
      continue; // unassigned fields are not part of any page's structure
    }
    const bucket = fieldsByPage.get(field.formPageId);
    if (bucket) {
      bucket.push(field);
    } else {
      fieldsByPage.set(field.formPageId, [field]);
    }
  }

  const snapshotPages: SnapshotPage[] = sortedPages.map((page) => {
    const pageFields = [...(fieldsByPage.get(page.id) ?? [])].sort(byDisplayOrderThenId);
    const snapshotFields: FormFieldDef[] = pageFields.map((field) => toFieldDef(field));

    const snapshotPage: SnapshotPage = {
      id: page.id,
      title: page.title,
      displayOrder: pageOrder.get(page.id) ?? 0,
      audienceSet: [...(page.audienceSet ?? DEFAULT_AUDIENCE_SET)],
      phase: page.phase ?? DEFAULT_PHASE,
      requiresDirectorApproval: page.requiresDirectorApproval ?? false,
      fields: snapshotFields,
    };
    if (page.helpText != null) {
      snapshotPage.helpText = page.helpText;
    }
    if (page.helpTextFormat != null) {
      snapshotPage.helpTextFormat = page.helpTextFormat;
    }
    if (page.helpTextHtml != null) {
      snapshotPage.helpTextHtml = page.helpTextHtml;
    }
    if (page.autoCollapseHelp != null) {
      snapshotPage.autoCollapseHelp = page.autoCollapseHelp;
    }
    if (page.visibleDivisionKeys !== undefined) {
      snapshotPage.visibleDivisionKeys =
        page.visibleDivisionKeys === null ? null : [...page.visibleDivisionKeys];
    }
    return snapshotPage;
  });

  const snapshot: FormVersionSnapshot = {
    formSchemaId: meta.formSchemaId ?? sortedPages[0]?.formSchemaId ?? '',
    versionNumber: meta.versionNumber ?? 1,
    pages: snapshotPages,
  };
  const competitionId = meta.competitionId ?? sortedPages[0]?.competitionId;
  if (competitionId !== undefined) {
    snapshot.competitionId = competitionId;
  }

  return deepFreeze(snapshot);
}

// ---------------------------------------------------------------------------
// diffStructure
// ---------------------------------------------------------------------------

/** Whether two `visibleDivisionKeys` selections are equal (order-insensitive). */
function sameDivisionKeys(
  a: readonly string[] | null | undefined,
  b: readonly string[] | null | undefined,
): boolean {
  const norm = (k: readonly string[] | null | undefined): string[] =>
    (k ?? []).filter((x) => typeof x === 'string').slice().sort();
  const na = norm(a);
  const nb = norm(b);
  return na.length === nb.length && na.every((x, i) => x === nb[i]);
}

/** Whether two audience sets are equal (order-insensitive). */
function sameAudienceSet(a: readonly Audience[], b: readonly Audience[]): boolean {
  const na = [...a].sort();
  const nb = [...b].sort();
  return na.length === nb.length && na.every((x, i) => x === nb[i]);
}

/** Whether two option lists are equal (order-sensitive — order is meaningful). */
function sameOptions(
  a: readonly string[] | undefined,
  b: readonly string[] | undefined,
): boolean {
  const na = a ?? [];
  const nb = b ?? [];
  return na.length === nb.length && na.every((x, i) => x === nb[i]);
}

/** Whether two field definitions differ in a non-type attribute. */
function fieldAttributesDiffer(prev: FormFieldDef, next: FormFieldDef): boolean {
  return (
    prev.label !== next.label ||
    prev.required !== next.required ||
    prev.bindingTarget !== next.bindingTarget ||
    prev.wordLimit !== next.wordLimit ||
    prev.characterLimit !== next.characterLimit ||
    prev.maxFileSizeBytes !== next.maxFileSizeBytes ||
    prev.maxFileCount !== next.maxFileCount ||
    prev.helpText !== next.helpText ||
    !sameOptions(prev.options, next.options) ||
    !sameOptions(prev.allowedContentTypes, next.allowedContentTypes) ||
    !sameDivisionKeys(prev.visibleDivisionKeys, next.visibleDivisionKeys) ||
    !sameAudienceSet(prev.audienceSet ?? [], next.audienceSet ?? [])
  );
}

/**
 * Compute the list of structural changes between two captured snapshots
 * (Req 8.2). Pages and fields are matched by id; a field that changes its
 * `fieldCategory` or `fieldType` is reported as `FIELD_RETYPED`, any other
 * field attribute change as `FIELD_MODIFIED`. Pure and total.
 */
export function diffStructure(
  prev: FormVersionSnapshot,
  next: FormVersionSnapshot,
): StructuralChange[] {
  const changes: StructuralChange[] = [];

  const prevPages = new Map(prev.pages.map((p) => [p.id, p]));
  const nextPages = new Map(next.pages.map((p) => [p.id, p]));

  // Page-level differences.
  for (const page of next.pages) {
    if (!prevPages.has(page.id)) {
      changes.push({
        kind: 'PAGE_ADDED',
        pageId: page.id,
        detail: `Page "${page.title}" was added.`,
      });
    }
  }
  for (const page of prev.pages) {
    const after = nextPages.get(page.id);
    if (!after) {
      changes.push({
        kind: 'PAGE_REMOVED',
        pageId: page.id,
        detail: `Page "${page.title}" was removed.`,
      });
      continue;
    }
    if (
      page.title !== after.title ||
      page.helpText !== after.helpText ||
      page.phase !== after.phase ||
      page.requiresDirectorApproval !== after.requiresDirectorApproval ||
      !sameAudienceSet(page.audienceSet, after.audienceSet) ||
      !sameDivisionKeys(page.visibleDivisionKeys, after.visibleDivisionKeys)
    ) {
      changes.push({
        kind: 'PAGE_MODIFIED',
        pageId: page.id,
        detail: `Page "${page.title}" attributes changed.`,
      });
    }
  }

  // Field-level differences (matched by id across all pages).
  const prevFields = fieldIndex(prev);
  const nextFields = fieldIndex(next);

  for (const [fieldId, { field }] of nextFields) {
    if (!prevFields.has(fieldId)) {
      changes.push({
        kind: 'FIELD_ADDED',
        fieldId,
        detail: `Field "${field.label}" was added.`,
      });
    }
  }
  for (const [fieldId, { field: before }] of prevFields) {
    const afterEntry = nextFields.get(fieldId);
    if (!afterEntry) {
      changes.push({
        kind: 'FIELD_REMOVED',
        fieldId,
        detail: `Field "${before.label}" was removed.`,
      });
      continue;
    }
    const after = afterEntry.field;
    if (before.fieldCategory !== after.fieldCategory || before.fieldType !== after.fieldType) {
      changes.push({
        kind: 'FIELD_RETYPED',
        fieldId,
        detail: `Field "${before.label}" changed type.`,
      });
    } else if (fieldAttributesDiffer(before, after)) {
      changes.push({
        kind: 'FIELD_MODIFIED',
        fieldId,
        detail: `Field "${before.label}" attributes changed.`,
      });
    }
  }

  return changes;
}

// ---------------------------------------------------------------------------
// immutabilityGuard
// ---------------------------------------------------------------------------

/**
 * Guard a structural edit against destroying data captured by prior
 * submissions. While at least one Application_Response has reached SUBMITTED
 * (`hasSubmittedResponses`), a Custom_Field that has a stored Response_Value
 * (its id is in `fieldIdsWithResponses`) may NOT be deleted or have its
 * Field_Type changed (Req 8.4); each such attempt is reported as a violation
 * naming the field (Req 8.5). Adding new (optional) fields is always allowed
 * (Req 8.6). Bound_Fields store no value, so they never appear in
 * `fieldIdsWithResponses` and are exempt by construction.
 *
 * When no submissions exist, every edit is allowed. Pure and total.
 */
export function immutabilityGuard(
  prev: FormVersionSnapshot,
  next: FormVersionSnapshot,
  fieldIdsWithResponses: ReadonlySet<string>,
  hasSubmittedResponses: boolean,
): ImmutabilityGuardResult {
  if (!hasSubmittedResponses) {
    return { allowed: true, violations: [] };
  }

  const violations: ImmutabilityViolation[] = [];
  const prevFields = fieldIndex(prev);
  const nextFields = fieldIndex(next);

  for (const [fieldId, { field: before }] of prevFields) {
    // Only Custom_Fields with stored values are protected; Bound_Fields are
    // exempt by construction (they hold no Response_Value).
    if (before.fieldCategory !== 'CUSTOM' || !fieldIdsWithResponses.has(fieldId)) {
      continue;
    }

    const afterEntry = nextFields.get(fieldId);
    if (!afterEntry) {
      violations.push({
        fieldId,
        reason: `Cannot delete field "${before.label}" because it has stored responses from submitted applications.`,
      });
      continue;
    }

    const after = afterEntry.field;
    if (before.fieldType !== after.fieldType || before.fieldCategory !== after.fieldCategory) {
      violations.push({
        fieldId,
        reason: `Cannot change the type of field "${before.label}" because it has stored responses from submitted applications.`,
      });
    }
  }

  return { allowed: violations.length === 0, violations };
}

// ---------------------------------------------------------------------------
// renderResponse
// ---------------------------------------------------------------------------

/**
 * Re-render a stored Application_Response through the version snapshot it was
 * captured against, so labels, options, ordering, phase, audience, and approval
 * flags match exactly what the applicant saw (Req 8.3, 9.3). Each Custom_Field
 * carries its stored Response_Value (absent when unanswered); Bound_Fields carry
 * no value (they resolve from their canonical entity elsewhere).
 *
 * Because the snapshot's page/field order is canonical and the stored value is
 * carried through unchanged, re-rendering reproduces the originally stored
 * Response_Values — the round-trip property (Req 8.7). Pure and total; neither
 * argument is mutated.
 */
export function renderResponse(
  snapshot: FormVersionSnapshot,
  values: Record<string, ResponseValue>,
): RenderedPage[] {
  return snapshot.pages.map((page) => {
    const fields: RenderedField[] = page.fields.map((field, index) => {
      const rendered: RenderedField = {
        fieldId: field.id,
        label: field.label,
        fieldCategory: field.fieldCategory,
        required: field.required,
        displayOrder: index,
      };
      if (field.bindingTarget !== undefined) {
        rendered.bindingTarget = field.bindingTarget;
      }
      if (field.fieldType !== undefined) {
        rendered.fieldType = field.fieldType;
      }
      // Only Custom_Fields carry a stored value; Bound_Fields never do.
      if (field.fieldCategory === 'CUSTOM') {
        const value = values[field.id];
        if (value !== undefined) {
          rendered.value = value;
        }
      }
      return rendered;
    });

    const renderedPage: RenderedPage = {
      pageId: page.id,
      title: page.title,
      displayOrder: page.displayOrder,
      phase: page.phase,
      audienceSet: [...page.audienceSet],
      requiresDirectorApproval: page.requiresDirectorApproval,
      fields,
    };
    if (page.helpText !== undefined) {
      renderedPage.helpText = page.helpText;
    }
    return renderedPage;
  });
}
